/**
 * POI Cost Tracking Integration
 * 
 * Intègre le cost tracking des POIs dans le système de coûts global
 * Remonte les données au dashboard admin existant
 */

import { logger } from '../../../utils/logger.js'
import CostTrackingService from '../services/cost-tracking.service.js'
import type { DashboardData } from '../services/cost-tracking.service.js'

// Singleton instance - partagé avec poi.controller.ts
let costTrackingInstance: CostTrackingService | null = null

export function getCostTrackingService(): CostTrackingService {
  if (!costTrackingInstance) {
    costTrackingInstance = new CostTrackingService(
      parseFloat(process.env.POI_API_BUDGET || '10')
    )
  }
  return costTrackingInstance
}

/**
 * Adapter pour convertir POI costs au format du dashboard admin existant
 */
export function adaptPOICostsForDashboard(poiDashboard: DashboardData): any {
  const { currentBudget, sourceBreakdown, zoomBreakdown, alerts, predictions, topUsers, hourlyMetrics } =
    poiDashboard

  // Map des sources POI aux providers génériques du dashboard
  const poiSourceToProvider: Record<string, string> = {
    overpass: 'overpass-api',
    mapbox: 'mapbox',
    google: 'google-places',
    wikipedia: 'wikipedia-api',
    custom: 'custom-db'
  }

  // Convertir breakdown par source en format provider
  const byProvider: Record<string, any> = {}
  for (const [source, data] of Object.entries(sourceBreakdown)) {
    const provider = poiSourceToProvider[source] || source
    byProvider[provider] = {
      cost: data.totalCost * 100, // Convert to cents
      usage: data.queryCount,
      percentage: data.costPercentage
    }
  }

  // Créer les budgets
  const budgets = Object.entries(sourceBreakdown).map(([source, data]) => ({
    id: `poi-${source}`,
    provider: poiSourceToProvider[source] || source,
    spent: data.totalCost * 100, // cents
    limit: currentBudget.limit * 100, // cents
    period: 'daily',
    status: currentBudget.status
  }))

  // Convertir les alertes
  const adaptedAlerts = alerts.map(alert => ({
    id: `poi-alert-${Date.now()}`,
    severity: alert.level === 'critical' ? 'critical' : alert.level === 'warning' ? 'high' : 'low',
    message: alert.message,
    timestamp: alert.timestamp,
    source: 'poi-system'
  }))

  // Suggestions d'optimisation
  const suggestions = []

  // Si Google est >50% des coûts
  if (sourceBreakdown.google && sourceBreakdown.google.costPercentage > 50) {
    suggestions.push({
      id: 'poi-reduce-google',
      title: 'Reduce Google Places API usage',
      description: 'Google Places costs are high. Consider using selective queries or lazy loading details.',
      priority: 'high',
      estimatedSavings: sourceBreakdown.google.totalCost * 0.3 * 100, // 30% savings (cents)
      impact: 8,
      effort: 'medium',
      actionable: false
    })
  }

  // Si cache hit rate < 50%
  const avgCacheHitRate = hourlyMetrics.reduce((sum, m) => sum + m.cacheHitRate, 0) / hourlyMetrics.length
  if (avgCacheHitRate < 50) {
    suggestions.push({
      id: 'poi-improve-cache',
      title: 'Improve cache hit rate',
      description: `Current cache hit rate is ${avgCacheHitRate.toFixed(0)}%. Increasing TTL can save costs.`,
      priority: 'medium',
      estimatedSavings: currentBudget.spent * 0.2 * 100, // 20% savings (cents)
      impact: 7,
      effort: 'low',
      actionable: false
    })
  }

  // Si zoom queries sont inefficaces
  const zoomUnder10 = zoomBreakdown.filter(z => z.zoomLevel < 10)
  const zoomUnder10Cost = zoomUnder10.reduce((sum, z) => sum + z.totalCost, 0)
  if (zoomUnder10Cost < currentBudget.spent * 0.1) {
    suggestions.push({
      id: 'poi-optimize-zoom',
      title: 'Optimize zoom-level queries',
      description: 'Most queries are at low zoom levels (free). Good optimization!',
      priority: 'low',
      estimatedSavings: 0,
      impact: 5,
      effort: 'low',
      actionable: false
    })
  }

  // Top users
  const topUsersList = topUsers.map((user, index) => ({
    userId: user.userId,
    userName: `User ${user.userId}`,
    usage: user.queries,
    cost: user.cost * 100 // cents
  }))

  return {
    success: true,
    data: {
      summary: {
        total: currentBudget.spent * 100, // cents
        byProvider,
        topUsers: topUsersList,
        trend: {
          change: predictions.trend === 'increasing' ? 5 : predictions.trend === 'decreasing' ? -5 : 0
        }
      },
      forecast: {
        nextMonth: predictions.projectedDaily * 30 * 100, // cents
        confidence: 0.7
      },
      alerts: adaptedAlerts,
      budgets,
      suggestions,
      rawData: {
        poiDashboard,
        budget: currentBudget,
        sourceBreakdown,
        zoomBreakdown,
        hourlyMetrics,
        predictions
      }
    }
  }
}

/**
 * Record a POI API cost event
 */
export function recordPOICost(
  source: string,
  cost: number,
  queries: number,
  poiCount: number,
  zoomLevel: number,
  userId?: string,
  cached: boolean = false
): void {
  const costTracking = getCostTrackingService()
  costTracking.recordCost(source, cost, queries, poiCount, zoomLevel, userId, cached)
  logger.debug(`Recorded POI cost: ${source} $${cost.toFixed(4)}`)
}

/**
 * Get POI dashboard data
 */
export function getPOIDashboardData(): any {
  const costTracking = getCostTrackingService()
  const poiDashboard = costTracking.getDashboard()
  return adaptPOICostsForDashboard(poiDashboard)
}

/**
 * Get POI budget status
 */
export function getPOIBudgetStatus(): any {
  const costTracking = getCostTrackingService()
  const budget = costTracking.getCurrentBudget()
  return {
    success: true,
    data: {
      date: budget.date,
      spent: budget.spent * 100, // cents
      limit: budget.limit * 100, // cents
      remaining: budget.remaining * 100, // cents
      percentage: budget.percentage,
      status: budget.status,
      projectedDaily: budget.projectedDaily,
      recommendations: generateBudgetRecommendations(budget)
    }
  }
}

/**
 * Generate smart recommendations based on budget
 */
function generateBudgetRecommendations(budget: any): string[] {
  const recommendations: string[] = []

  if (budget.status === 'critical') {
    recommendations.push('🚨 Budget critical! Switching to free sources only (OVERPASS, Wikipedia)')
    recommendations.push('⛔ Disabling Google Places API queries')
  } else if (budget.status === 'warning') {
    recommendations.push('⚠️ Budget warning! Selective use of paid APIs recommended')
    recommendations.push('💡 Focus on cache hits to reduce API calls')
  } else {
    recommendations.push('✅ Budget healthy. All sources available.')
    recommendations.push('💡 Cache hit rate is good, continue monitoring')
  }

  if (budget.projectedDaily > budget.limit) {
    recommendations.push(`📈 Projected cost ($${budget.projectedDaily}) exceeds daily limit ($${budget.limit})`)
    recommendations.push('💡 Consider increasing daily budget or optimizing queries')
  }

  return recommendations
}

export default {
  getCostTrackingService,
  adaptPOICostsForDashboard,
  recordPOICost,
  getPOIDashboardData,
  getPOIBudgetStatus
}
